<script lang="ts">
  import ModalDialog from "@kontextwerk/web-sdk/ui/overlay/ModalDialog.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiArrowRight, mdiBookOpenPageVariantOutline } from "@mdi/js"
  import {
    currentBanner,
    removeBanner,
    upsertBanner,
  } from "@kontextwerk/web-sdk/core/runtime/bannerStore"
  import { location } from "../../../../_shared/_logic/store"
  import { spaNavigate } from "../../../../_shared/_logic/navigation"
  import { fetchLearningLoginId } from "../../../../api/kehrblatt/learning-guide"
  import { learningBasePath } from "../../subviews/Learning/config"
  import {
    getLearningLessonHref,
    getLearningPathProgress,
  } from "../../_shared/_logic/learning-path"
  import { getLearningProgress } from "../../_shared/_logic/learning-progress"
  import { claimLearningLoginReminder } from "./_logic/login-reminder"

  const { organization, userId } = $props<{ organization: string; userId: string }>()
  const progress = getLearningProgress()
  const basePath = $derived(learningBasePath(organization))
  const course = $derived(progress.course)
  const remaining = $derived(course ? course.total_count - course.completed_count : 0)
  const next = $derived(course ? getLearningPathProgress(course.lessons).nextLesson : null)
  const nextHref = $derived(next ? getLearningLessonHref(basePath, next) : basePath)
  const importantNotice = $derived(($currentBanner?.priority ?? -10) > -10)
  const inLearning = $derived(
    $location.path === basePath || $location.path.startsWith(`${basePath}/`),
  )
  const onHome = $derived(
    $location.path === `/organizations/${encodeURIComponent(organization)}/overview` &&
      [null, "0", "home"].includes(new URLSearchParams($location.search).get("tab")),
  )
  let login = $state<{ userId: string; id: string | null } | null>(null)
  let prompt = $state<{ userId: string; loginId: string } | null>(null)
  const instanceId = $props.id()
  const bannerId = `kehrblatt-learning-${instanceId}`

  $effect(() => {
    const requestedUser = userId
    let current = true
    void fetchLearningLoginId().then(
      (id) => {
        if (current) login = { userId: requestedUser, id }
      },
      () => {
        if (current) login = { userId: requestedUser, id: null }
      },
    )

    return () => {
      current = false
    }
  })

  $effect(() => {
    const currentLogin = login?.userId === userId ? login.id : null
    const currentUser = userId
    const canRemind = course !== null && !progress.error && remaining > 0
    const reading = inLearning || importantNotice
    let current = true

    if (importantNotice) closePrompt()

    if (currentLogin && canRemind) {
      void claimLearningLoginReminder(currentUser, currentLogin).then((claimed) => {
        if (current && claimed && !reading) prompt = { userId: currentUser, loginId: currentLogin }
      })
    }

    return () => {
      current = false
    }
  })

  $effect(() => {
    const visible = course && !progress.error && remaining > 0 && !inLearning && !onHome
    const href = nextHref

    if (visible) {
      upsertBanner({
        id: bannerId,
        title:
          remaining === 1 ? "Noch eine Anleitung offen" : `Noch ${remaining} Anleitungen offen`,
        severity: "info",
        priority: -10,
        dismissible: false,
        pulse: false,
        action: { label: "Weiterlernen", onClick: () => spaNavigate(href) },
      })
    } else {
      removeBanner(bannerId)
    }

    return () => removeBanner(bannerId)
  })

  function closePrompt() {
    prompt = null
  }

  function continueLearning() {
    closePrompt()
    spaNavigate(nextHref)
  }
</script>

{#if prompt && prompt.userId === userId && prompt.loginId === login?.id && course && remaining > 0 && !inLearning}
  <ModalDialog
    title="KehrBlatt Schritt für Schritt kennenlernen"
    appearance="plain"
    onClose={closePrompt}
    size="medium"
  >
    <div class="learning-login">
      <span class="learning-login__icon" aria-hidden="true">
        <Icon path={mdiBookOpenPageVariantOutline} size="1.6rem" />
      </span>
      <p>Die bebilderten Anleitungen zeigen Ihnen, wie Sie KehrBlatt im Arbeitsalltag nutzen.</p>
      <p class="learning-login__count"
        >{course.completed_count} von {course.total_count} Anleitungen erledigt</p
      >
      <progress
        max={course.total_count}
        value={course.completed_count}
        aria-label="Persönlicher Lernfortschritt"
      ></progress>
      {#if next}<p class="learning-login__next">Als Nächstes: <strong>{next.title}</strong></p>{/if}
    </div>
    {#snippet footerSnippet()}
      <div class="learning-login__actions">
        <button type="button" class="learning-login__later" onclick={closePrompt}>Zur Arbeit</button
        >
        <button type="button" class="learning-login__continue" onclick={continueLearning}>
          <Icon path={mdiArrowRight} size="1.1rem" />Jetzt weiterlernen
        </button>
      </div>
    {/snippet}
  </ModalDialog>
{/if}

<style>
  .learning-login {
    color: var(--text-invers-100);
    font-size: 16px;
    line-height: 1.5;
  }
  .learning-login__icon {
    display: inline-flex;
    color: #7c2024;
    margin-bottom: 0.5rem;
  }
  p {
    margin: 0 0 1rem;
  }
  .learning-login__count {
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  .learning-login__next {
    margin: 1rem 0 0;
  }
  progress {
    display: block;
    appearance: none;
    width: 100%;
    height: 8px;
    border: 0;
    background: #e8ecee;
    color: #28794a;
  }
  progress::-webkit-progress-bar {
    background: #e8ecee;
  }
  progress::-webkit-progress-value {
    background: #28794a;
  }
  progress::-moz-progress-bar {
    background: #28794a;
  }
  .learning-login__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.75rem;
  }
  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    min-height: 44px;
    padding: 0.65rem 0.9rem;
    border: 1px solid #b7c3c8;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
    color: var(--text-invers-100);
    background: #fff;
  }
  button:focus-visible {
    outline: 3px solid #344e5a;
    outline-offset: 3px;
  }
  .learning-login__continue {
    color: #fff;
    border-color: #7c2024;
    background: #7c2024;
  }
  @media (max-width: 480px) {
    .learning-login__actions {
      flex-direction: column-reverse;
    }
    button {
      width: 100%;
    }
  }
</style>
